import { TableSortLabel } from "@mui/material";
import StatusChip from "components/shared/StatusChip";
import TextSkeleton from "components/shared/TextSkeleton";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { useTranslation } from "react-i18next";
import type { AdvanceProvisionRow, AdvanceProvisionSortKey } from "store/advanceProvision";
import { formatMonthRange } from "store/advanceProvision/advance-provision-rules";

type Column = {
    key: keyof AdvanceProvisionRow;
    group: "Identification" | "Financial" | "Time" | "Provision";
    sortable: boolean;
    align?: "right";
    /** Free text that may wrap instead of widening the table */
    wraps?: boolean;
};

const COLUMNS: Column[] = [
    { key: "beneficiaryName", group: "Identification", sortable: true, wraps: true },
    { key: "workName", group: "Identification", sortable: true, wraps: true },
    { key: "workCode", group: "Identification", sortable: true },
    { key: "termReference", group: "Identification", sortable: true },
    { key: "compensationGroupName", group: "Identification", sortable: false, wraps: true },
    { key: "status", group: "Identification", sortable: true },
    { key: "contractualAdvance", group: "Financial", sortable: true, align: "right" },
    { key: "paidAdvance", group: "Financial", sortable: true, align: "right" },
    { key: "recoupedRoyalties", group: "Financial", sortable: true, align: "right" },
    { key: "unrecoupedBalance", group: "Financial", sortable: true, align: "right" },
    { key: "firstPublicationDate", group: "Time", sortable: true },
    { key: "referenceDate", group: "Time", sortable: true },
    { key: "elapsedMonths", group: "Time", sortable: true, align: "right" },
    { key: "slice", group: "Provision", sortable: false },
    { key: "rate", group: "Provision", sortable: true, align: "right" },
    { key: "provisionAmount", group: "Provision", sortable: true, align: "right" },
];

const GROUPS = ["Identification", "Financial", "Time", "Provision"] as const;
/** First column of every group but the first: carries the group separator down the whole table */
const GROUP_START_KEYS = new Set(
    COLUMNS.filter((column, index) => index > 0 && column.group !== COLUMNS[index - 1].group).map(
        (column) => column.key,
    ),
);
const groupBorder = (key: keyof AdvanceProvisionRow) =>
    GROUP_START_KEYS.has(key) ? "border-l border-border-color" : "";
/** Placeholder rows while a page loads: enough to fill the visible table, not the whole page size */
const SKELETON_ROW_COUNT = 10;
const headerCell = "bg-backgroundZ2 px-3 py-2 text-left align-bottom font-normal text-foreground";
const groupHeaderCell = [
    headerCell,
    "border-b border-l border-border-color font-medium whitespace-nowrap first:border-l-0",
].join(" ");

const money = (cents: number) => formatNumber(cents / 100, { isCurrency: true, always2Digit: true });
// Calendar dates (yyyy-mm-dd) are UTC midnight: format them in UTC so they do not shift a day back
const formatCalendarDate = (value: string | null) =>
    value ? formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }) : "—";

const AdvanceProvisionCell = ({
    row,
    columnKey,
}: {
    row: AdvanceProvisionRow;
    columnKey: keyof AdvanceProvisionRow;
}) => {
    const [t] = useTranslation();
    const statusRules = {
        published: { color: "green", text: t("AdvanceProvision.Status.published") },
        unpublished: { color: "yellow", text: t("AdvanceProvision.Status.unpublished") },
        ended: { color: "grey", text: t("AdvanceProvision.Status.ended") },
    };

    switch (columnKey) {
        case "status":
            return <StatusChip status={row.status} statusRules={statusRules} />;
        case "contractualAdvance":
        case "paidAdvance":
        case "recoupedRoyalties":
        case "unrecoupedBalance":
            return money(row[columnKey]);
        case "provisionAmount":
            return <span className="font-medium">{money(row.provisionAmount)}</span>;
        case "firstPublicationDate":
        case "referenceDate":
            return formatCalendarDate(row[columnKey]);
        case "rate":
            return `${formatNumber(row.rate)} %`;
        case "slice":
            if (row.overrideLabel) {
                return (
                    <span className="whitespace-nowrap rounded border border-brand bg-brand-light px-2 py-0.5 text-brand">
                        {row.overrideLabel}
                    </span>
                );
            }
            return row.slice ? (
                <span className="rounded border border-border-color bg-backgroundZ1 px-2 py-0.5 whitespace-nowrap">
                    {t("AdvanceProvision.Management.SliceBadge", { range: formatMonthRange(row.slice) })}
                </span>
            ) : (
                "—"
            );
        default:
            return row[columnKey] ?? "—";
    }
};

type Props = {
    rows: AdvanceProvisionRow[];
    isLoading: boolean;
    sort: AdvanceProvisionSortKey;
    order: "asc" | "desc";
    onSort: (key: AdvanceProvisionSortKey) => void;
};

/** Provision rows at the selected closing date. */
const AdvanceProvisionTable = ({ rows, isLoading, sort, order, onSort }: Props) => {
    const [t] = useTranslation();

    return (
        <div className="relative max-h-[55vh] overflow-auto rounded border border-border-color bg-white">
            <table className="w-full border-separate border-spacing-0 text-xs">
                <thead className="sticky top-0 z-10">
                    <tr>
                        {GROUPS.map((group) => (
                            <th
                                key={group}
                                colSpan={COLUMNS.filter((column) => column.group === group).length}
                                className={groupHeaderCell}
                            >
                                {t(`AdvanceProvision.Management.Groups.${group}`)}
                            </th>
                        ))}
                    </tr>
                    <tr>
                        {COLUMNS.map(({ key, sortable, align }) => (
                            <th
                                key={key}
                                className={`${headerCell} ${groupBorder(key)} ${align === "right" ? "text-right" : ""}`}
                            >
                                {sortable ? (
                                    <TableSortLabel
                                        active={sort === key}
                                        direction={sort === key ? order : "asc"}
                                        onClick={() => onSort(key as AdvanceProvisionSortKey)}
                                    >
                                        {t(`AdvanceProvision.Management.Columns.${key}`)}
                                    </TableSortLabel>
                                ) : (
                                    t(`AdvanceProvision.Management.Columns.${key}`)
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {isLoading &&
                        Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                            <tr key={`skeleton-${index}`}>
                                {COLUMNS.map(({ key }) => (
                                    <td
                                        key={key}
                                        className={`border-t border-border-color px-3 py-2 ${groupBorder(key)}`}
                                    >
                                        <TextSkeleton width="100%" />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    {!isLoading &&
                        rows.map((row) => (
                            <tr key={row.termId}>
                                {COLUMNS.map(({ key, align, wraps }) => (
                                    <td
                                        key={key}
                                        className={`border-t border-border-color px-3 py-2 ${groupBorder(key)} ${
                                            wraps ? "min-w-[8rem]" : "whitespace-nowrap"
                                        } ${align === "right" ? "text-right" : ""}`}
                                    >
                                        <AdvanceProvisionCell row={row} columnKey={key} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                </tbody>
            </table>
            {!isLoading && !rows.length && (
                <p className="py-8 text-center text-sm text-foreground">{t("AdvanceProvision.Management.Empty")}</p>
            )}
        </div>
    );
};

export default AdvanceProvisionTable;
